import {
  FunctionComponent,
  useCallback,
  useEffect,
  useState,
  type JSX,
} from 'react';

import * as userActions from '@payaca/store/user/userActions';

import { useSelector } from '@/api/state';
import { Button, Modal } from '@payaca/ambrosia';
import { useDispatch } from 'react-redux';

type Props = {
  userId: number;
  isOpen: boolean;
  onClose?: () => void;
  onMakePrimaryUserSuccess?: () => void;
};

const ConfirmMakePrimaryUserModal: FunctionComponent<Props> = ({
  userId,
  isOpen,
  onClose,
  onMakePrimaryUserSuccess,
}: Props): JSX.Element => {
  const dispatch = useDispatch();
  const [isSubmitted, setIsSubmitted] = useState(false);

  const isMakingPrimaryUser = useSelector((state) => {
    return state.user.isMakingPrimaryUser;
  });
  const isPrimaryUserMadeSuccessfully = useSelector((state) => {
    return state.user.isPrimaryUserMadeSuccessfully;
  });

  const onConfirm = useCallback(() => {
    setIsSubmitted(true);
    dispatch(userActions.requestMakePrimaryUser(userId));
  }, [userId, dispatch]);

  useEffect(() => {
    if (isSubmitted && isPrimaryUserMadeSuccessfully) {
      onMakePrimaryUserSuccess?.();
    }
  }, [isSubmitted, isPrimaryUserMadeSuccessfully, onMakePrimaryUserSuccess]);

  return (
    <Modal isOpen={isOpen} title={'Make primary user'} onClose={onClose}>
      <Modal.Body>
        <ul>
          <li>
            The primary user&#39;s details will be associated with your
            subscription payment.
          </li>
          <li>
            The primary user is the first person we will contact in relation to
            your account.
          </li>
          <li>
            If you make someone else the primary user, you will not be able to
            undo this action.
          </li>
        </ul>
        <p style={{ textAlign: 'center' }}>
          <strong>Are you sure you want to change the primary user?</strong>
        </p>
      </Modal.Body>
      <Modal.Footer>
        <Modal.Footer.Actions>
          <Button
            onClick={() => !isMakingPrimaryUser && onConfirm()}
            isProcessing={isMakingPrimaryUser}
            replaceContentWhenProcessing
          >
            Yes, confirm
          </Button>
        </Modal.Footer.Actions>
      </Modal.Footer>
    </Modal>
  );
};

export default ConfirmMakePrimaryUserModal;
